import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { removeUserAPI } from 'src/api';
import { ToastStyled } from 'src/components';
import { useCurrentUser } from 'src/hooks';
import type { MembershipStatus, OrgQueriesData, UserProfile } from 'src/types';

export const useRemoveUser = () => {
  const { user } = useCurrentUser();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      email,
      status,
    }: {
      id: string;
      email: string;
      status: MembershipStatus;
    }) => await removeUserAPI(id, email, status),
    onSuccess: (_, { id, email }) => {
      toast.custom((t) => (
        <ToastStyled variant='success' handleClose={() => toast.dismiss(t)}>
          {email === user!.email
            ? 'You left the organization successfully!'
            : 'User removed successfully!'}
        </ToastStyled>
      ));

      // remove the user from all queries that depend on the orgUsers query
      if (email === user!.email) {
        queryClient.invalidateQueries({
          queryKey: ['cart', 'list'],
          type: 'all',
        });

        try {
          queryClient.setQueryData(['currentUser'], (oldData: UserProfile) => {
            if (!oldData) {
              return undefined;
            }

            const updatedData = {
              ...oldData,
              subscription: {
                tier: 'free',
                status: 'active',
              },
            };

            return updatedData;
          });
        } catch (err) {
          queryClient.invalidateQueries({
            queryKey: ['currentUser'],
            type: 'all',
          });
        }
      }

      try {
        queryClient.setQueriesData(
          {
            queryKey: ['orgUsers'],
            type: 'all',
          },
          (
            oldData:
              | { pageParams: number[]; pages: OrgQueriesData[] }
              | undefined,
          ) => {
            if (!oldData) {
              return undefined;
            }

            const updatedCache = oldData.pages.map((page) => ({
              ...page,
              users: page.users.filter((user) => user.id !== id),
            }));

            return {
              ...oldData,
              pages: updatedCache,
            };
          },
        );
      } catch (err) {
        console.error(JSON.stringify(err, null, 2));

        queryClient.invalidateQueries({
          queryKey: ['orgUsers'],
          type: 'all',
        });
      }

      // if the user is removing themself, redirect to the cart history page
      if (email === user!.email) {
        navigate('/plus/cart-history');
      }
    },
    onError: (error, { email }) => {
      if (error && error.message === 'last_admin') {
        toast.custom(
          (t) => (
            <ToastStyled variant='error' handleClose={() => toast.dismiss(t)}>
              You cannot leave the organization as the last admin.
              <br />
              <br />
              Visit the{' '}
              <Link to='/plus/manage-subscription' className='font-bold'>
                Manage Subscription
              </Link>{' '}
              page to disband the organization.
            </ToastStyled>
          ),
          { duration: 6000 },
        );
      } else {
        toast.custom((t) => (
          <ToastStyled variant='error' handleClose={() => toast.dismiss(t)}>
            {email === user!.email
              ? 'Failed to leave the organization.'
              : `Failed to remove ${email}.`}
          </ToastStyled>
        ));
      }
    },
  });
};
